import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import ComboBox from 'Components/ComboBox'
import { makeGetRequest, makePostRequest } from 'utils/api'

import Typography from '@mui/material/Typography'
import { Box, Stack } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt'

export default function QuestionAnswerForm({ updateReports, startIcon, open, setOpen }) {
  const [questionanswer, setQuestionanswer] = useState([])
  const [ans, setAns] = useState({})
  const [loading, setLoading] = useState(false)

  const { handleSubmit, reset } = useForm({})

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setAns({})
    reset()
  }

  const onSubmit = async () => {
    const payload = {
      ans: Object.values(ans),
    }

    const response = await makePostRequest(
      '/api/Professional/question-softskills/',
      payload,
      {},
      true,
    )
    if (response.status === 200) {
      updateReports()
      handleClose()
    }
  }

  const fetchQuestionanswer = useCallback(async () => {
    setLoading(true)
    const url = '/api/Professional/question-softskills/'
    const { data, status } = await makeGetRequest(url, {}, true)
    if (status === 200) {
      setQuestionanswer(data)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    if (open) {
      setAns({})
      fetchQuestionanswer()
    }
  }, [fetchQuestionanswer, open])

  const answeredCount = Object.keys(ans).length
  const totalQuestions = questionanswer?.length || 0
  const progress = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0

  return (
    <>
      <Box sx={{ cursor: 'pointer' }} onClick={handleClickOpen}>
        {startIcon}
      </Box>
      <Dialog
        fullWidth
        maxWidth="md"
        className="no-drag"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: '#f7f9fc',
            boxShadow: '0 28px 80px rgba(6, 22, 48, 0.35)',
            maxHeight: '92vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: '#ffffff !important',
            bgcolor: '#06295f',
            px: { xs: 2.5, sm: 4 },
            py: 3,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Stack direction="row" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'rgba(255, 255, 255, 0.14)',
                }}
              >
                <PsychologyAltIcon />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: 20, sm: 24 }, lineHeight: 1.1 }}>
                  Questionnaire Soft Skills
                </Typography>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.72)', fontSize: 14, mt: 0.75 }}>
                  {answeredCount} / {totalQuestions || 0} réponses sélectionnées
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={handleClose} sx={{ color: 'white' }} aria-label="Fermer">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {loading && <LinearProgress />}
          <DialogContent
            sx={{
              px: { xs: 2.5, sm: 4 },
              py: 3,
              minHeight: 0,
              overflowY: 'auto',
            }}
          >
            <Box
              sx={{
                height: 8,
                borderRadius: 999,
                bgcolor: '#e4ebf5',
                mb: 3,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${progress}%`,
                  height: '100%',
                  borderRadius: 999,
                  bgcolor: '#1b75d0',
                  transition: 'width 180ms ease',
                }}
              />
            </Box>
            <Stack gap={2}>
              {questionanswer &&
                questionanswer.map((val, idx) => {
                  const questionKey = val.question_id || idx

                  return (
                    <Box
                      key={questionKey}
                      sx={{
                        border: '1px solid #dbe4f0',
                        borderRadius: 2,
                        bgcolor: '#ffffff',
                        p: { xs: 2, sm: 2.5 },
                      }}
                    >
                      <Typography
                        component="div"
                        sx={{ color: '#12233d', fontWeight: 700, mb: 1.5, lineHeight: 1.35 }}
                      >
                        <Box component="span" sx={{ color: '#64748b', mr: 1 }}>
                          {idx + 1}.
                        </Box>
                        {val.question_id__question_text}
                      </Typography>
                      <ComboBox
                        handleAutoComplete={(e, selectedAnswer) => {
                          setAns((previousAnswers) => {
                            const nextAnswers = { ...previousAnswers }
                            if (selectedAnswer?.id) {
                              nextAnswers[questionKey] = selectedAnswer.id
                            } else {
                              delete nextAnswers[questionKey]
                            }
                            return nextAnswers
                          })
                        }}
                        data={val.ans}
                        required={true}
                        sx={{ width: '100%', mt: 0 }}
                      />
                    </Box>
                  )
                })}
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{
              px: { xs: 2.5, sm: 4 },
              py: 2,
              gap: 1,
              bgcolor: '#ffffff',
              borderTop: '1px solid #dbe4f0',
              flexShrink: 0,
            }}
          >
            <Button
              onClick={() => {
                handleClose()
              }}
              sx={{ color: '#526173', fontWeight: 700 }}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!totalQuestions || answeredCount < totalQuestions}
              sx={{
                bgcolor: '#1b75d0',
                borderRadius: 999,
                px: 3,
                fontWeight: 800,
                textTransform: 'none',
                '&:hover': { bgcolor: '#145da8' },
              }}
            >
              Valider mes réponses
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  )
}
